import React, { useEffect, useMemo, useState } from 'react';
import { User, ChevronDown } from 'lucide-react';
import MeetingMap from '../tm/MeetingMap';
import { apiClient } from '../../api/apiClient';
import { HQ_MEETING_API } from '../tm/meetingMapApi';
import { isAdminUser } from './adminlayout/sidebarFilters';
import { useToast } from '../../components/Toast';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdOutlineBtn,
  stdSearchWrap, stdSearchIcon, stdSearchSelect, stdSearchChevron,
} from './adminlayout/designStandard';
import { MeetingMapSettingsModal } from './meetingmap/MeetingMapSettingsModal';

const DEFAULT_SETTINGS_KEYS = ['MEETING_MAP_MANAGEMENT'];

// 마지막에 본 마케팅담당자를 기억(브라우저 localStorage). 다음에 열 때 그 담당자를 다시 보여준다.
//   회사 스코프된 목록에 그 담당자가 없으면(다른 회사/퇴사) 첫 번째 담당자로 폴백. PINE 직원명 하드코딩 없음.
const LAST_SALES_KEY = 'meetingMap.lastSalesName';
const MOBILE_BREAKPOINT = 768;

// api: 관리자 주소 묶음(2026-10-22) — 본사 = HQ_MEETING_API(예전 그대로). 회사별·통합 화면이 자기 묶음을 넘긴다.
//   settingsKeys: ⚙️ 설정 버튼을 보일 열쇠(하나라도 있으면). 회사별·통합 화면은 그 화면 열쇠를 더 넘긴다(2026-10-22).
export default function AdminMeetingMap({
  api = HQ_MEETING_API,
  subtitle = '전체 미팅 일정·결과를 지도로 관리합니다.',
  settingsKeys = DEFAULT_SETTINGS_KEYS,
} = {}) {
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesList, setSalesList] = useState([]);
  const [selectedSalesName, setSelectedSalesName] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 설정 모달을 닫을 때마다 증가 → MeetingMap이 배너·피드백 옵션·인계 담당자를 재조회
  //   (2026-09-02: 설정 변경이 새로고침 없이는 반영 안 되던 문제 수정)
  const [mapRefreshToken, setMapRefreshToken] = useState(0);
  // 설정 버튼은 **미팅지도 관리 권한이 있을 때만** 보인다 (2026-09-15, fail-closed).
  //   전에는 "테넌트 관리자면 숨김"이었다. 권한과 무관한 조건이라 테넌트는 같은 메뉴를 배정받고도
  //   설정을 못 썼고(사용자 신고), 권한 없는 직원이 주소창으로 들어오면 눌러도 403 인 버튼이 보였다.
  //   설정이 이제 테넌트별이라 테넌트를 숨길 이유는 없고, 기준은 권한 하나다.
  //   권한을 모르는 동안(로딩·실패)은 숨긴다. 실제 차단은 서버(requireMeetingMapAdmin)가 한다.
  //   /api/my/permissions 는 드릴다운 제외 대상이라 **보는 사람 본인** 권한이다.
  const [canManageSettings, setCanManageSettings] = useState(false);

  useEffect(() => {
    fetchSalesPeople();
    let active = true;
    apiClient.get('/api/my/permissions').then((r) => {
      const codes = r?.ok && Array.isArray(r.data?.permCodes) ? r.data.permCodes : [];
      if (active) setCanManageSettings(isAdminUser() || settingsKeys.some((k) => codes.includes(k)));
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchSalesPeople() {
    setLoadingSales(true);
    const result = await apiClient.get(api.salesPeople);
    setLoadingSales(false);

    if (!result.ok) {
      showToast(result.data?.message || '마케팅담당자 목록을 불러오지 못했습니다.', 'error');
      return;
    }

    const list = Array.isArray(result.data?.list) ? result.data.list : [];
    const normalized = list
      .map((row) => String(row?.name || '').trim())
      .filter(Boolean);
    setSalesList(normalized);

    // 마지막에 본 담당자가 현재 목록에 있으면 그대로, 없으면 첫 번째 담당자. 목록이 비면 빈 값.
    if (normalized.length > 0) {
      let saved = '';
      try { saved = localStorage.getItem(LAST_SALES_KEY) || ''; } catch (_) { /* ignore */ }
      setSelectedSalesName(normalized.includes(saved) ? saved : normalized[0]);
    } else {
      setSelectedSalesName('');
    }
  }

  function handleSelectSales(name) {
    const value = String(name || '').trim();
    setSelectedSalesName(value);
    try { if (value) localStorage.setItem(LAST_SALES_KEY, value); } catch (_) { /* ignore */ }
  }

  const salesOptions = useMemo(() => {
    if (!selectedSalesName) return salesList;
    if (salesList.includes(selectedSalesName)) return salesList;
    return [selectedSalesName, ...salesList];
  }, [salesList, selectedSalesName]);

  return (
    <div>
      {/* ── 페이지 헤더: 제목/부제(좌) + 설정 버튼(우) — 직원 관리 표준 (카드 박스·그림자 제거) ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>미팅지도 관리</h2>
          <div style={stdPageSubtitle}>{subtitle}</div>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {canManageSettings && (
            <button
              type="button"
              style={stdOutlineBtn}
              onClick={() => setSettingsOpen(true)}
              title="미팅지도 설정 — 피드백 옵션·미팅인계 담당자·배너 문구"
              aria-label="미팅지도 설정"
            >
              ⚙️{isMobile ? '' : ' 설정'}
            </button>
          )}
        </div>
      </div>

      {/* 담당자 선택 — 제목 아래, 출퇴근 관리의 '이름 검색' 칸과 같은 자리·모양 (2026-09-16 사용자 요청) */}
      <div style={{ ...stdSearchWrap(isMobile), maxWidth: isMobile ? '100%' : 240, marginBottom: 16 }}>
        <User size={15} style={stdSearchIcon} />
        <select
          id="admin-meeting-map-sales"
          style={stdSearchSelect}
          value={selectedSalesName}
          disabled={loadingSales || salesOptions.length === 0}
          onChange={(e) => handleSelectSales(e.target.value)}
          aria-label="마케팅담당자 선택"
        >
          {salesOptions.length === 0 && <option value="">담당자 없음</option>}
          {salesOptions.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
        {/* 사파리에서 기본 화살표가 사라지므로 직접 얹는다 */}
        <ChevronDown size={16} style={stdSearchChevron} />
      </div>

      <MeetingMapSettingsModal
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setMapRefreshToken((t) => t + 1); }}
      />

      {selectedSalesName ? (
        <MeetingMap adminMode adminSalesName={selectedSalesName} refreshToken={mapRefreshToken} api={api} />
      ) : (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          {loadingSales ? '불러오는 중...' : '마케팅담당자가 없습니다.'}
        </div>
      )}
    </div>
  );
}
